import React from 'react';
import wrap from 'wraptile';
import {ButtonBase, FormControlLabel, Grid, Switch} from '@material-ui/core';
import KeyboardIcon from '@material-ui/icons/Keyboard';
import {OnChangeSwitchEvent} from '@pluto/@types';
import QCTooltip from '@pluto/qc-tooltip';
import VolumeControls from './VolumeControls/VolumeControls';
import MarkerControls from './MarkerControls/MarkerControls';
import PlayPauseButton from './PlayPauseButton/PlayPauseButton';
import {
    FullScreenIcon,
    JumpBackwardIcon,
    JumpForwardIcon,
    NextIcon,
    PreviousIcon,
} from 'VideoAudioQC/icons';
import {PlaybackSpeedChange} from '../VideoAudioQCTypes';
import './PlayerControls.scss';

export interface PlayerControlsProps {
    paused: boolean;
    onStepOneFrameBack: () => void;
    onStepOneFrameForward: () => void;
    onPlay: () => void;
    onPause: () => void;
    onFiveSecondsForward: () => void;
    onFiveSecondsBack: () => void;
    onVolumeChange: () => void;
    volume: number;
    onToggleMute: () => void;
    muteButtonDisabled: boolean;
    muted: boolean;
    onFullscreen: () => void;
    onSetIn: () => void;
    onSetOut: () => void;
    onClearPoints: () => void;
    onGoToIn: () => void;
    onGoToOut: () => void;
    onNextMarker: () => void;
    onPreviousMarker: () => void;
    onOpenHotkeys: () => void;
    showSafeMargins: boolean;
    onSafeMarginsChange: OnChangeSwitchEvent;
    jobDeliveryDate: string;
    playbackSpeed: number;
    onPlaybackSpeedChange: PlaybackSpeedChange;
    playbackSpeedOptions: number[];
}

export const onHandleOpenHotkeys = wrap(
    (onOpenHotkeys: () => void, event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        onOpenHotkeys();
    }
);

export default function PlayerControls(props: PlayerControlsProps) {
    const {
        paused,
        onStepOneFrameBack,
        onStepOneFrameForward,
        onPlay,
        onPause,
        onFiveSecondsForward,
        onFiveSecondsBack,
        onVolumeChange,
        volume,
        onToggleMute,
        muteButtonDisabled,
        muted,
        onFullscreen,
        onNextMarker,
        onPreviousMarker,
        onOpenHotkeys,
        showSafeMargins,
        onSafeMarginsChange,
    } = props;

    const handleOpenHotkeys = onHandleOpenHotkeys(onOpenHotkeys);

    return (
        <Grid container alignItems="center" className="root-container">
            <Grid item xs={1}>
                <div className="flex-container">
                    <QCTooltip title="Step 5 seconds back (Shift + Ctrl + Down)" arrow>
                        <ButtonBase
                            onClick={onFiveSecondsBack}
                            aria-label="Step 5 seconds back"
                            aria-keyshortcuts="Shift+Ctrl+Down"
                        >
                            <JumpBackwardIcon />
                        </ButtonBase>
                    </QCTooltip>
                    <QCTooltip title="Step 1 frame back (Left)" arrow>
                        <ButtonBase
                            onClick={onStepOneFrameBack}
                            aria-label="Step 1 frame back"
                            aria-keyshortcuts="Left"
                        >
                            <PreviousIcon />
                        </ButtonBase>
                    </QCTooltip>
                    <PlayPauseButton paused={paused} onPlay={onPlay} onPause={onPause} />
                    <QCTooltip title="Step 1 frame forward (Right)" arrow>
                        <ButtonBase
                            onClick={onStepOneFrameForward}
                            aria-label="Step 1 frame forward"
                            aria-keyshortcuts="Right"
                        >
                            <NextIcon />
                        </ButtonBase>
                    </QCTooltip>
                    <QCTooltip title="Step 5 seconds forward (Shift + Ctrl + Up)" arrow>
                        <ButtonBase
                            onClick={onFiveSecondsForward}
                            aria-label="Step 5 seconds forward"
                            aria-keyshortcuts="Shift+Ctrl+Up"
                        >
                            <JumpForwardIcon />
                        </ButtonBase>
                    </QCTooltip>
                </div>
            </Grid>

            <Grid item xs={2}></Grid>

            <Grid item xs={3}>
                <div className="flex-container around-container">
                    <div className="volume-controls">
                        <VolumeControls
                            value={volume}
                            muted={muted}
                            onToggleMute={onToggleMute}
                            muteButtonDisabled={muteButtonDisabled}
                            volumeChange={onVolumeChange}
                            aria-label="Volume controls"
                        />
                    </div>
                    <QCTooltip title="Toggle fullscreen (F)" arrow>
                        <ButtonBase onClick={onFullscreen} aria-label="Toggle fullscreen">
                            <FullScreenIcon />
                        </ButtonBase>
                    </QCTooltip>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showSafeMargins}
                                onChange={onSafeMarginsChange}
                                name="Safe margins"
                                color="primary"
                                size="small"
                            />
                        }
                        label="Safe margins"
                        labelPlacement="start"
                    />
                </div>
            </Grid>
            <Grid item xs={3}></Grid>
            <Grid item xs={2}>
                <MarkerControls
                    onNextMarker={onNextMarker}
                    onPreviousMarker={onPreviousMarker}
                />
            </Grid>
            <Grid item xs={1}>
                <div className="flex-container hotkey">
                    <QCTooltip title="Show Hotkeys (shift+/)" arrow>
                        <ButtonBase onClick={handleOpenHotkeys} aria-label="Show Hotkeys">
                            <KeyboardIcon />
                        </ButtonBase>
                    </QCTooltip>
                </div>
            </Grid>
        </Grid>
    );
}
